import { FACING_ONLY_TRIGGERS, INTERNAL_ONLY_TRIGGERS, isFacingTrigger, planAtLeast, WIDGET_ONLY_TRIGGERS, WORKFLOW_TRIGGER_GROUPS, WORKFLOW_TRIGGER_MIN_PLAN, type WorkflowTrigger, type WorkflowTriggerGroup } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Icon } from "@sagot/ui";
import type { KeyboardEvent } from "react";
import { useWorkspaceDetails } from "../../../session/workspace-context";
import { useServiceDeskState } from "./service/service-desk-context";
import { serviceDeskBlocked } from "./service/use-service-desk";

const W = "engage.workflows";
const GROUPS = Object.keys(WORKFLOW_TRIGGER_GROUPS) as WorkflowTriggerGroup[];
/** The Service desk triggers a workflow cannot start on by choice: timers and hand-offs the Service Desk raises itself (`contracts/service-desk/workflow-triggers.json`, `internal_only`). A default listens to them; they are not offered for a new workflow. */
const NOT_OFFERED: ReadonlySet<string> = new Set(INTERNAL_ONLY_TRIGGERS);

const ARROW_STEP: Readonly<Record<string, 1 | -1>> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

/** Arrow keys move through the radios and select the one they land on (the radio pattern); Tab still reaches each. */
export function arrowThroughRadios(event: KeyboardEvent<HTMLButtonElement>): void {
  const step = ARROW_STEP[event.key];
  if (step === undefined) return;
  const radios = [...(event.currentTarget.closest('[role="radiogroup"]')?.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)') ?? [])];
  const from = radios.findIndex((radio) => radio === document.activeElement);
  if (from < 0) return;
  event.preventDefault();
  const next = radios[(from + step + radios.length) % radios.length];
  next?.focus();
  next?.click();
}

interface TriggerPickerProps {
  value: WorkflowTrigger;
  onChange: (trigger: WorkflowTrigger) => void;
}

/** Every trigger in its group with what it is for; customer-facing ones say so, plan-gated ones name their plan. */
export function TriggerPicker({ value, onChange }: TriggerPickerProps) {
  const { t } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan;
  const serviceDesk = useServiceDeskState();
  const blocked = serviceDeskBlocked(serviceDesk.state);
  return (
    <div className="max-h-[50vh] space-y-4 overflow-y-auto pr-1" role="radiogroup" aria-label={t(`${W}.builder.trigger`)}>
      {GROUPS.map((group) => (
        <section key={group}>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-fg-subtle">{t(`${W}.groups.${group}`)}</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {WORKFLOW_TRIGGER_GROUPS[group].filter((key) => key === value || !NOT_OFFERED.has(key)).map((key) => {
              const needs = WORKFLOW_TRIGGER_MIN_PLAN[key];
              const planNeeded = needs !== undefined && plan !== undefined && !planAtLeast(plan, needs) ? needs : null;
              const unavailable = group === "service" && blocked;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={value === key}
                  disabled={planNeeded !== null || unavailable}
                  className="rounded-lg border border-line p-3 text-left hover:border-primary aria-checked:border-primary aria-checked:bg-primary-soft disabled:cursor-not-allowed disabled:bg-sunken disabled:text-fg-subtle disabled:hover:border-line"
                  onClick={() => { onChange(key); }}
                  onKeyDown={arrowThroughRadios}
                >
                  <span className="block text-sm font-bold">{t(`${W}.triggers.${key}`)}</span>
                  <span className="block text-xs text-fg-subtle">{t(`${W}.triggerHints.${key}`)}</span>
                  <span className="mt-2 flex flex-wrap gap-1.5 empty:hidden">
                    {isFacingTrigger(key) ? <Badge tone="brand">{t(`${W}.kinds.${FACING_ONLY_TRIGGERS.has(key) ? "facingOnly" : "either"}`)}</Badge> : null}
                    {WIDGET_ONLY_TRIGGERS.has(key) ? <Badge>{t(`${W}.kinds.widgetOnly`)}</Badge> : null}
                    {planNeeded ? (
                      <Badge>
                        <Icon name="lock" className="size-3" />
                        {t(`${W}.planNeeded`, { plan: t(`console.plans.${planNeeded}`) })}
                      </Badge>
                    ) : null}
                    {unavailable ? (
                      <Badge data-testid="service-trigger-locked">
                        <Icon name="lock" className="size-3" />
                        {serviceDesk.state === "locked" ? t(`${W}.planNeeded`, { plan: t(`console.plans.${serviceDesk.minPlan}`) }) : t(`${W}.service.notSwitchedOn`)}
                      </Badge>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
